import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import ActionTypes from '@utils/deviceActions';
import rolesConstants from '@utils/rolesConstants';
import { Form, FormInstance, Radio } from 'antd';
import { IDevice } from '@pages/Assets/Inventory/types';
import useSuspension from './useSuspension';
import useWithdrawalOfSuspension from './useWithdrawalOfSuspension';
import useCancellationOfSuspension from './useCancellationOfSuspension';

interface SuspensionWithdrawalProps {
  devices: IDevice[];
  form: FormInstance<any>;
  isUpload?: boolean;
}

const useSuspensionWithdrawal = ({
  devices,
  form,
  isUpload,
}: SuspensionWithdrawalProps): DeviceActionProps => {
  const { t } = useTranslation([
    'actions-modal',
    'device-actions',
    'attributes',
  ]);
  const suspensionType = Form.useWatch('suspensionType', form);

  const devicesPayload = useMemo(
    (): { msisdn: string }[] =>
      devices?.map(device => ({
        msisdn: device.msisdn,
      })),
    [devices],
  );

  const suspensionAction = useSuspension(devicesPayload, form);
  const withdrawalOfSuspensionAction =
    useWithdrawalOfSuspension(devicesPayload);
  const cancellationOfSuspensionAction =
    useCancellationOfSuspension(devicesPayload);

  const selectedAction: DeviceActionProps = useMemo(() => {
    const actions: { [key: number]: DeviceActionProps } = {
      [ActionTypes.SUSPENSION]: suspensionAction,
      [ActionTypes.WITHDRAWAL_OF_SUSPENSION]: withdrawalOfSuspensionAction,
      [ActionTypes.CANCELLATION_SUSPENSION]: cancellationOfSuspensionAction,
    };

    return actions[suspensionType];
  }, [
    cancellationOfSuspensionAction,
    suspensionType,
    suspensionAction,
    withdrawalOfSuspensionAction,
  ]);

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const defaultSteps: DeviceActionStepsProps[] = [
      {
        title: t('suspension-withdrawal.action'),
        name: 'suspensionType',
        showWhenUpload: true,
        content: (
          <Radio.Group optionType="button" disabled={isUpload && !devices}>
            <Radio value={ActionTypes.SUSPENSION}>
              {t('suspension-withdrawal.suspend')}
            </Radio>
            <Radio value={ActionTypes.WITHDRAWAL_OF_SUSPENSION}>
              {t('suspension-withdrawal.withdrawal')}
            </Radio>
            <Radio value={ActionTypes.CANCELLATION_SUSPENSION}>
              {t('suspension-withdrawal.cancel')}
            </Radio>
          </Radio.Group>
        ),
      },
    ];

    if (selectedAction?.steps) {
      defaultSteps.push(...selectedAction?.steps);
    }

    return defaultSteps;
  }, [devices, isUpload, selectedAction?.steps, t]);

  return {
    ...selectedAction,
    payloadToSend: {
      ...selectedAction?.payloadToSend,
      action: selectedAction?.payloadToSend?.action ?? ActionTypes.SUSPENSION,
    },
    title: t(
      `device-actions:labels.mot.${rolesConstants.SUSPENSION_WITHDRAWAL}`,
    ),
    steps,
  };
};

export default useSuspensionWithdrawal;
